// ═══════════════════════════════════════════════════════════════
// Handoff — Analytics
// Metrics, status breakdowns, completion rates
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import {
  Title, Text, Paper, SimpleGrid, Stack, Group, Badge, Progress,
  Select,
} from '../../components/gab/GabComponents';
import { useHandoffStore } from '../../store/handoffStore';
import { useAuthStore } from '../../store/authStore';
import { primary, semantic } from '../../lib/colors';

export default function HandoffAnalytics() {
  const { notes, actionItems, events: handoffEvents, fetchAll } = useHandoffStore();
  const { activeIncident, loadIncidents } = useAuthStore();

  useEffect(() => {
    loadIncidents();
    fetchAll();
  }, []);

  // Store already filtered to active incident
  const filteredNotes = notes;
  const filteredActions = actionItems;
  const filteredEvents = handoffEvents;

  const totalEvents = filteredEvents.length;
  const activeEvents = filteredEvents.filter(e => e.status === 'active').length;
  const closedEvents = filteredEvents.filter(e => e.status === 'closed').length;

  const totalNotes = filteredNotes.length;
  const drafts = filteredNotes.filter(n => n.status === 'draft').length;
  const submitted = filteredNotes.filter(n => n.status === 'submitted').length;
  const acknowledged = filteredNotes.filter(n => n.status === 'acknowledged').length;
  const ackRate = totalNotes > 0 ? Math.round(((acknowledged) / Math.max(submitted + acknowledged, 1)) * 100) : 0;

  const totalActions = filteredActions.length;
  const pending = filteredActions.filter(a => a.status === 'pending').length;
  const completed = filteredActions.filter(a => a.status === 'completed').length;
  const overdue = filteredActions.filter(a => a.status === 'pending' && a.dueDate && new Date(a.dueDate) < new Date()).length;
  const completionRate = totalActions > 0 ? Math.round((completed / totalActions) * 100) : 0;

  const dayNotes = filteredNotes.filter(n => n.fromShift === 'day').length;
  const nightNotes = filteredNotes.filter(n => n.fromShift === 'night').length;

  // Top contributors
  const contributors: Record<string, number> = {};
  filteredNotes.filter(n => n.senderName).forEach(n => {
    contributors[n.senderName] = (contributors[n.senderName] || 0) + 1;
  });
  const topContrib = Object.entries(contributors).sort((a, b) => b[1] - a[1]).slice(0, 10);

  // Per-event breakdown
  const eventBreakdown = filteredEvents.map(e => ({
    event: e,
    notes: filteredNotes.filter(n => n.eventId === e.id).length,
    actions: filteredActions.filter(a => a.eventId === e.id).length,
    pendingActions: filteredActions.filter(a => a.eventId === e.id && a.status === 'pending').length,
  })).filter(e => e.notes > 0 || e.actions > 0).sort((a, b) => b.notes - a.notes);

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Handoff Analytics</Title>
        {activeIncident && <Text size="sm" c="dimmed">{activeIncident.name}</Text>}
      </Group>

      {/* Key Metrics */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md">
        <Paper p="md" radius="sm" withBorder ta="center" style={{ borderTop: `3px solid ${primary[700]}` }}>
          <Text fz={28} fw={700} c={primary[700]}>{filteredEvents.length}</Text>
          <Text size="xs" c="dimmed">Total Events</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder ta="center" style={{ borderTop: '3px solid var(--data-viz-250)' }}>
          <Text fz={28} fw={700} c="var(--data-viz-250)">{totalNotes}</Text>
          <Text size="xs" c="dimmed">Total Handoffs</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder ta="center" style={{ borderTop: `3px solid ${semantic.success}` }}>
          <Text fz={28} fw={700} c={semantic.success}>{ackRate}%</Text>
          <Text size="xs" c="dimmed">Ack Rate</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder ta="center" style={{ borderTop: `3px solid ${semantic.info}` }}>
          <Text fz={28} fw={700} c={semantic.info}>{completionRate}%</Text>
          <Text size="xs" c="dimmed">Action Completion</Text>
        </Paper>
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, md: 3 }} mb="md">
        {/* Event Status */}
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">Event Status</Text>
          <Stack gap="xs">
            <div>
              <Group justify="space-between" mb={2}><Text size="xs">Active</Text><Text size="xs" fw={500}>{activeEvents}</Text></Group>
              <Progress value={totalEvents > 0 ? (activeEvents / totalEvents) * 100 : 0} color="var(--color-success)" size="sm" radius="xl" />
            </div>
            <div>
              <Group justify="space-between" mb={2}><Text size="xs">Closed</Text><Text size="xs" fw={500}>{closedEvents}</Text></Group>
              <Progress value={totalEvents > 0 ? (closedEvents / totalEvents) * 100 : 0} color="#939598" size="sm" radius="xl" />
            </div>
          </Stack>
        </Paper>

        {/* Handoff Status */}
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">Handoff Status</Text>
          <Stack gap="xs">
            <div>
              <Group justify="space-between" mb={2}><Text size="xs">Draft</Text><Text size="xs" fw={500}>{drafts}</Text></Group>
              <Progress value={totalNotes > 0 ? (drafts / totalNotes) * 100 : 0} color="#939598" size="sm" radius="xl" />
            </div>
            <div>
              <Group justify="space-between" mb={2}><Text size="xs">Submitted</Text><Text size="xs" fw={500}>{submitted}</Text></Group>
              <Progress value={totalNotes > 0 ? (submitted / totalNotes) * 100 : 0} color="#C68700" size="sm" radius="xl" />
            </div>
            <div>
              <Group justify="space-between" mb={2}><Text size="xs">Acknowledged</Text><Text size="xs" fw={500}>{acknowledged}</Text></Group>
              <Progress value={totalNotes > 0 ? (acknowledged / totalNotes) * 100 : 0} color="var(--color-success)" size="sm" radius="xl" />
            </div>
          </Stack>
        </Paper>

        {/* Action Status */}
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">Action Items</Text>
          <Stack gap="xs">
            <div>
              <Group justify="space-between" mb={2}><Text size="xs">Pending</Text><Text size="xs" fw={500}>{pending}</Text></Group>
              <Progress value={totalActions > 0 ? (pending / totalActions) * 100 : 0} color="#03AC9E" size="sm" radius="xl" />
            </div>
            <div>
              <Group justify="space-between" mb={2}><Text size="xs" c={semantic.error}>Overdue</Text><Text size="xs" fw={500} c={semantic.error}>{overdue}</Text></Group>
              <Progress value={totalActions > 0 ? (overdue / totalActions) * 100 : 0} color="var(--color-error)" size="sm" radius="xl" />
            </div>
            <div>
              <Group justify="space-between" mb={2}><Text size="xs">Completed</Text><Text size="xs" fw={500}>{completed}</Text></Group>
              <Progress value={totalActions > 0 ? (completed / totalActions) * 100 : 0} color="var(--color-success)" size="sm" radius="xl" />
            </div>
          </Stack>
        </Paper>
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, md: 2 }}>
        {/* Shift Split */}
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">Shift Distribution</Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <Paper p="sm" radius="sm" bg="#FDF8F1" ta="center">
              <Text fz={24} fw={700} c={semantic.warning}>{dayNotes}</Text>
              <Text size="xs" c={semantic.warning}>Day Shift</Text>
            </Paper>
            <Paper p="sm" radius="sm" bg="#F1F0FC" ta="center">
              <Text fz={24} fw={700} c={primary[700]}>{nightNotes}</Text>
              <Text size="xs" c={primary[700]}>Night Shift</Text>
            </Paper>
          </SimpleGrid>
        </Paper>

        {/* Top Contributors */}
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">Top Contributors</Text>
          {topContrib.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center">No data yet</Text>
          ) : (
            <Stack gap="xs">
              {topContrib.map(([name, count], i) => (
                <Group key={name} justify="space-between">
                  <Text size="sm">{i + 1}. {name}</Text>
                  <Badge size="xs" variant="light" color="blurple">{count} handoffs</Badge>
                </Group>
              ))}
            </Stack>
          )}
        </Paper>
      </SimpleGrid>

      {/* Per-Event Table */}
      {eventBreakdown.length > 0 && (
        <Paper p="md" radius="sm" withBorder mt="md">
          <Text fw={600} size="sm" mb="sm">By Event</Text>
          <div style={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--frost)' }}>
                  <th style={{ padding: '8px 16px', textAlign: 'center' }}>Event</th>
                  <th style={{ padding: '8px 16px', textAlign: 'center' }}>Status</th>
                  <th style={{ padding: '8px 16px', textAlign: 'center' }}>Handoffs</th>
                  <th style={{ padding: '8px 16px', textAlign: 'center' }}>Actions</th>
                  <th style={{ padding: '8px 16px', textAlign: 'center' }}>Pending</th>
                </tr>
              </thead>
              <tbody>
                {eventBreakdown.map(row => (
                  <tr key={row.event.id} style={{ borderBottom: '1px solid var(--frost)' }}>
                    <td style={{ padding: '8px 16px', textAlign: 'center' }}>{row.event.eventName}</td>
                    <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                      <Badge size="xs" color={row.event.status === 'active' ? 'var(--color-success)' : '#939598'} tt="capitalize">{row.event.status}</Badge>
                    </td>
                    <td style={{ padding: '8px 16px', textAlign: 'center' }}>{row.notes}</td>
                    <td style={{ padding: '8px 16px', textAlign: 'center' }}>{row.actions}</td>
                    <td style={{ padding: '8px 16px', textAlign: 'center', color: row.pendingActions > 0 ? 'var(--color-warning)' : undefined }}>
                      {row.pendingActions}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Paper>
      )}
    </div>
  );
}
